import type { ManagementTimelineQuery } from '@internal/spassu-code-contract';
import type { Dispatch, MutableRefObject } from 'react';
import { useEffect } from 'react';
import {
  cockpitNavigableStateKey,
  normalizeCockpitNavigableState,
} from '../navigation/cockpitQueryState';
import type {
  CockpitNavigableState,
  CockpitNavigationPort,
} from './CockpitDataSource';
import type {
  CockpitControllerAction,
  CockpitControllerState,
} from './cockpitControllerTypes';
import type { CockpitRequestCoordinator } from './cockpitRequestCoordinator';

type SnapshotLoader = (revision?: number, polling?: boolean) => Promise<void>;
type TimelineLoader = (
  query: ManagementTimelineQuery,
  reset: boolean,
) => Promise<void>;

export function useCockpitControllerLifecycle({
  activityId,
  navigationRef,
  dispatch,
  loadSnapshot,
  loadTimeline,
  snapshotRequests,
  timelineRequests,
}: {
  activityId: string;
  navigationRef: MutableRefObject<CockpitNavigationPort | undefined>;
  dispatch: Dispatch<CockpitControllerAction>;
  loadSnapshot: SnapshotLoader;
  loadTimeline: TimelineLoader;
  snapshotRequests: CockpitRequestCoordinator;
  timelineRequests: CockpitRequestCoordinator;
}): void {
  useEffect(() => {
    const initial = navigationRef.current?.initialState;
    const query = initial?.timelineQuery ?? {};
    dispatch({ type: 'reset', activityId, timelineQuery: query });
    void loadSnapshot(initial?.revision);
    void loadTimeline(query, true);
    return () => {
      snapshotRequests.cancel();
      timelineRequests.cancel();
    };
  }, [
    activityId,
    dispatch,
    loadSnapshot,
    loadTimeline,
    navigationRef,
    snapshotRequests,
    timelineRequests,
  ]);
}

export function useCockpitNavigationSync(
  state: CockpitControllerState,
  navigationRef: MutableRefObject<CockpitNavigationPort | undefined>,
  pendingNavigationRef: MutableRefObject<string | null>,
): void {
  const navigableState = navigableStateFromController(state);
  const stateKey = cockpitNavigableStateKey(navigableState);
  useEffect(() => {
    if (
      pendingNavigationRef.current &&
      pendingNavigationRef.current !== stateKey
    ) {
      return;
    }
    if (pendingNavigationRef.current === stateKey) {
      pendingNavigationRef.current = null;
    }
    navigationRef.current?.onChange?.(navigableState);
  }, [navigableState, navigationRef, pendingNavigationRef, stateKey]);
}

interface NavigationInputContext {
  activityId: string;
  state: CockpitControllerState;
  navigationRef: MutableRefObject<CockpitNavigationPort | undefined>;
  observedNavigationRef: MutableRefObject<string | null>;
  pendingNavigationRef: MutableRefObject<string | null>;
  dispatch: Dispatch<CockpitControllerAction>;
  loadSnapshot: SnapshotLoader;
  loadTimeline: TimelineLoader;
}

export function useCockpitNavigationInput(
  context: NavigationInputContext,
): void {
  const { navigationRef, state } = context;
  const external = navigationRef.current?.currentState;
  const externalKey = external
    ? navigationRef.current?.stateKey ?? cockpitNavigableStateKey(external)
    : null;
  const internalKey = cockpitNavigableStateKey(
    navigableStateFromController(state),
  );
  useEffect(() => {
    synchronizeNavigationInput(context, external, externalKey, internalKey);
  }, [context, external, externalKey, internalKey]);
}

function synchronizeNavigationInput(
  context: NavigationInputContext,
  external: Partial<CockpitNavigableState> | undefined,
  externalKey: string | null,
  internalKey: string,
): void {
  const { observedNavigationRef, pendingNavigationRef } = context;
  if (!external || !externalKey) {
    observedNavigationRef.current = null;
    pendingNavigationRef.current = null;
    return;
  }
  const identity = `${context.activityId}:${externalKey}`;
  if (observedNavigationRef.current === identity) return;
  const firstObservation = observedNavigationRef.current === null;
  observedNavigationRef.current = identity;
  if (externalKey === internalKey) {
    pendingNavigationRef.current = null;
    return;
  }
  pendingNavigationRef.current = externalKey;
  if (context.state.activityId !== context.activityId || firstObservation)
    return;
  applyExternalNavigation(context, external);
}

function applyExternalNavigation(
  context: NavigationInputContext,
  external: Partial<CockpitNavigableState>,
): void {
  const next = normalizeCockpitNavigableState(external);
  const current = navigableStateFromController(context.state);
  if (next.revision !== current.revision)
    void context.loadSnapshot(next.revision);
  const nextTimelineKey = cockpitNavigableStateKey({
    timelineQuery: next.timelineQuery,
  });
  const currentTimelineKey = cockpitNavigableStateKey({
    timelineQuery: current.timelineQuery,
  });
  if (nextTimelineKey === currentTimelineKey) return;
  context.dispatch({ type: 'timelineQueryChanged', query: next.timelineQuery });
  void context.loadTimeline(next.timelineQuery, true);
}

function navigableStateFromController(
  state: CockpitControllerState,
): CockpitNavigableState {
  const revision = state.snapshot?.historical
    ? state.snapshot.revision ?? undefined
    : undefined;
  return {
    ...(revision === undefined ? {} : { revision }),
    timelineQuery: state.timeline.query,
  };
}
